/**
 * Операционная карточка Confirm-Gate (§8) для форм тренерского workspace (P2).
 *
 * Тот же контракт, что у карточек чата (chat.ts, W-мутации): первый вызов
 * gated-мутации ничего не пишет — сервер выдаёт черновик (operation_id +
 * payload_hash, I-5); «Подтвердить» → `POST /operations/{id}/confirm`
 * исполняет тул серверно, «Отклонить» → reject снимает черновик. Слот один:
 * новый черновик заменяет старый (D2), повторный confirm — 404 (pop-first),
 * расхождение hash — 409.
 *
 * chat.ts НЕ трогаем: карточка чата живёт своей копией (W-контракт W2/W4) —
 * этот модуль нужен формам workspace (расписание/сообщение/программа/фаза),
 * которые монтируют карточку вне лога чата.
 */

import {
  type OperationCard,
  ApiError,
  confirmOperation,
  rejectOperation,
} from "./api";
import { getLang, t } from "./i18n";

// kind черновика → i18n-лейбл (`ops.kind.*`); неизвестный kind — ops.kind.other.
const KNOWN_KINDS = new Set([
  "schedule_upsert",
  "coach_message",
  "coach_broadcast",
  "coach_relay",
  "program_save",
  "program_phase",
  "own_program",
]);

const kindLabel = (kind: string): string =>
  KNOWN_KINDS.has(kind) ? t(`ops.kind.${kind}`) : t("ops.kind.other");

const formatTime = (iso: string): string => {
  try {
    return new Intl.DateTimeFormat(getLang(), {
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

function draftRow(key: string, value: unknown): HTMLElement {
  const row = document.createElement("div");
  row.className = "opcard__row";
  const k = document.createElement("span");
  k.className = "opcard__key";
  k.textContent = key;
  const v = document.createElement("span");
  v.className = "opcard__val";
  const text =
    typeof value === "object" && value !== null
      ? JSON.stringify(value)
      : String(value);
  v.textContent = text.length > 120 ? text.slice(0, 117) + "…" : text;
  row.append(k, v);
  return row;
}

/** Карточка черновика в контейнере формы; onDone — после confirm/reject. */
export function renderOperationCard(
  container: HTMLElement,
  card: OperationCard,
  onDone?: (ok: boolean) => void,
): HTMLElement {
  const el = document.createElement("div");
  el.className = "opcard";
  el.dataset.op = card.operation_id;

  const title = document.createElement("div");
  title.className = "opcard__title";
  title.textContent = t("ops.title");

  const kind = document.createElement("div");
  kind.className = "opcard__kind";
  kind.textContent = kindLabel(card.kind);

  const fields = document.createElement("div");
  fields.className = "opcard__fields";
  for (const [key, value] of Object.entries(card.draft ?? {})) {
    fields.appendChild(draftRow(key, value));
  }

  const exp = document.createElement("div");
  exp.className = "opcard__expiry";
  exp.textContent = `${t("ops.expires")} ${formatTime(card.expires_at)}`;

  const actions = document.createElement("div");
  actions.className = "opcard__actions";
  const confirmBtn = document.createElement("button");
  confirmBtn.className = "opcard__confirm";
  confirmBtn.textContent = t("ops.confirm");
  const rejectBtn = document.createElement("button");
  rejectBtn.className = "opcard__reject";
  rejectBtn.textContent = t("ops.reject");
  actions.append(confirmBtn, rejectBtn);

  const status = document.createElement("div");
  status.className = "opcard__status";
  status.hidden = true;

  el.append(title, kind, fields, exp, actions, status);
  container.appendChild(el);

  const setBusy = (on: boolean) => {
    confirmBtn.disabled = on;
    rejectBtn.disabled = on;
  };

  /** Статус вместо кнопок (keep=false) или под ними (retry-случай сети). */
  const setStatus = (text: string, mod: string, keepButtons = false): void => {
    // display, а не hidden: .opcard__actions стилизован display:flex.
    actions.style.display = keepButtons ? "flex" : "none";
    status.hidden = false;
    status.textContent = text;
    status.className = `opcard__status opcard__status--${mod}`;
  };

  const closeCard = (text: string, mod: string, doneOk: boolean): void => {
    setStatus(text, mod, false);
    onDone?.(doneOk);
  };

  confirmBtn.addEventListener("click", () =>
    void (async () => {
      setBusy(true);
      try {
        const res = await confirmOperation(card.operation_id, card.payload_hash);
        const receiptOk = Boolean(
          (res.receipt as { ok?: boolean } | undefined)?.ok,
        );
        // Даже сбой тула — черновик израсходован (pop-first): карточка
        // закрывается честным вердиктом, повторить нельзя.
        closeCard(
          receiptOk ? t("ops.confirmed") : t("ops.failed"),
          receiptOk ? "confirmed" : "failed",
          receiptOk,
        );
      } catch (err) {
        const code = err instanceof ApiError ? err.status : 0;
        if (code === 404 || code === 409) {
          // I-5/слот: черновик сменён или израсходован — карточка устарела.
          closeCard(
            code === 409 ? t("ops.mismatch") : t("ops.expired"),
            "expired",
            false,
          );
        } else {
          // Сеть: операция могла исполниться — повтор даст 404 (устарело).
          setStatus(t("ops.failed"), "failed", true);
          setBusy(false);
        }
      }
    })(),
  );

  rejectBtn.addEventListener("click", () =>
    void (async () => {
      setBusy(true);
      try {
        await rejectOperation(card.operation_id);
      } catch {
        /* reject идемпотентен по смыслу: карточка гаснет в любом случае */
      }
      closeCard(t("ops.rejected"), "rejected", false);
    })(),
  );

  return el;
}
